import Auth0 from 'auth0-js';
import EventEmitter from 'events';
import merge from 'lodash.merge';
import { promisify } from './promisify';

// if promise hasn't been polyfilled polyfill it just for this file
const _Promise = typeof Promise === 'undefined' ? require('es6-promise').Promise : Promise;

promisify.Promise = _Promise;

/// quickstart
/// https://auth0.com/docs/quickstart/spa/vanillajs

const DEFAULT_OPTIONS = {
  redirectRoute: '',
  domain: 'cimpress.auth0.com',
  audience: 'https://api.cimpress.io/',
  scope: 'openid profile email user_id',

  // number of seconds offset - value of 30 means token will be considered expired 30 seconds before it actually expires with the provider
  expirationOffset: 30,

  // Emit the "tokenExpired" event if the token is expired at the time of client subscription (e.g. auth.on('tokenExpired', ...))
  // "expirationOffset" is used when determining whether the initial "tokenExpired" event should be triggered or not.
  emitInitialTokenExpired: true,

  // Emit the "authenticated" event if the token is not expired at the time of client subscription (e.g. auth.on('authenticated', ...))
  emitInitialAuthenticated: true,

  // number of seconds of clock skew to tolerate (see https://auth0.com/docs/libraries/lock/v10/customization#leeway-integer-)
  leeway: 30
};

export default class CentralizedAuth {
  // constructor options:
  //   clientID (required)
  //   redirectRoute (optional, see default above)
  //   domain (optional, see default above)
  //   audience (optional, see default above)
  //   scope (optional, see default above)
  //   expirationOffset (optional, see default above)
  //   emitInitialTokenExpired (optional, see default above)
  //   emitInitialAuthenticated (optional, see default above)
  //   leeway (optional, see default above)
  //   lockWidgetOptions (optional, see defaults in newLockWidget)
  constructor(options) {
    merge(this, DEFAULT_OPTIONS, options);
    this.redirectUri = window.location.origin + this.redirectRoute;

    this.auth0 = new Auth0.WebAuth({
      domain: this.domain,
      clientID: this.clientID,
      responseType: 'id_token token',
      audience: this.audience,
      scope: this.scope,
      redirectUri: this.redirectUri, //auth0 redirects back to this page after authentication
      leeway: this.leeway // avoid the "The token was issued in the future. Please check your computed clock." error
    });

    this.events = new EventEmitter();

    // listen for changes to localStorage that don't originate from this current window/tab
    window.addEventListener('storage', this.listenToStorage);
  }

  // Subscribe to events that the auth wrapper emits
  // Subscribable event types:
  // 1. tokenExpired: (expirationTime) => 
  //    * Fired when the amount of time the token is good for (minus the "expirationOffset") has elapsed
  //    * Fired if there exists an expired token in localStorage at the time of subscription
  //      - (configurable via "emitInitialTokenExpired" option)
  // 2. logout:
  //    * Fired when user is logged out
  // 3. authenticated: (source) =>
  //    * Fired if there exists an unexpired token in localStorage at the time of subscription
  //      - (configurable via "emitInitialAuthenticated" option)
  //      - source will be === "initial" in the provided callback for this scenario
  //      - it is also implied that the current window/tab is the source of the event
  //    * Fired when "this" instance of the authWrapper saves the user's login information to localStorage
  //      - source will be === "this" to denote that the event is coming from this instance
  //    * If the current window/tab does not have focus (i.e. blurred), this is fired when a new expiration is set
  //      in localStorage from a different window/tab with the same origin (e.g. 2 copies of the same app and you
  //      sign-in to one)
  //      - source will be === "storage" in the provided callback to denote that the authentication
  //        happened in a different window/tab
  on = (eventType, ...args) => {
    this.events.on(eventType, ...args);

    if (eventType === 'tokenExpired' && this.emitInitialTokenExpired) {
      const expiration = this.getExpiration();
      if (expiration && expiration - (this.expirationOffset * 1000) <= new Date().getTime()) {
        this.events.emit('tokenExpired', expiration);
      }
    }

    if (eventType === 'authenticated' && this.emitInitialAuthenticated) {
      if (this.isLoggedIn()) {
        this.events.emit('authenticated', 'initial');
      }
    }
  };

  removeListener = (eventType, ...args) => {
    this.events.removeListener(eventType, ...args);
  };

  // Assumes the token details have already been stored to local storage.
  setExpirationTimer = () => {
    // Get the time that the access token will expire at
    const expiresAt = localStorage.getItem('expires_at');

    if (!expiresAt) {
      return;
    }

    try {
      this.clearTimeout();
      const timeToWait = expiresAt - new Date().getTime() - (1000 * this.expirationOffset);
      this.expiresTimeout = window.setTimeout(
        () => this.events.emit('tokenExpired', Number.parseInt(expiresAt, 10)),
        timeToWait,
      );
    } catch (err) {
      console.warn('Failed to set token expiration monitor');
    }
  };

  // It is possible to create an infinite loop between 2 tabs/windows if localStorage is modified
  // within this listener - so don't do it :) 
  listenToStorage = (e) => {
    switch(e.key) {
      // TODO: add storage namespace option (e.g. "expires_at" -> "saw:expires_at") apps will likely break this wrapper's
      // behavior if they use any of the localStorage keys this wrapper relies on
      case 'expires_at': 
        // check to see if it's being removed or not
        if (e.newValue) {
          try {
            const expiresAt = JSON.parse(e.newValue);
            this.setExpirationTimer(expiresAt);
            this.events.emit('authenticated', 'storage');
          } catch (e) {
            // for JSON.parse: shouldn't happen unless people are modifying storage manually
          }
        }
        break;
      default:
    }
  };

  // Check whether the current time is past the access token's expiry time.
  // This method ignores the expiration offset option.
  isLoggedIn = () => {
    try {
      const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
      return new Date().getTime() < expiresAt;
    } catch (e) {
      return false;
    }
  };

  saveToken = (token, accessToken, profile, expiresIn) => {
    localStorage.setItem('token', token);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('profile', JSON.stringify(profile));

    if (expiresIn) {
      // Set the time that the access token will expire at
      const expiresAt = expiresIn * 1000 + new Date().getTime();
      // This will trigger listenToStorage on "blurred" tabs/windows of the same origin
      // It will not trigger this.listenToStorage in the current tab/window because that's not how StorageEvent's work
      localStorage.setItem('expires_at', JSON.stringify(expiresAt));

      this.events.emit('authenticated', 'this');
    }
  };

  clearTimeout = () => this.expiresTimeout = window.clearTimeout(this.expiresTimeout);

  removeToken = () => {
    this.clearTimeout();
    localStorage.removeItem('expires_at');
    localStorage.removeItem('token');
    localStorage.removeItem('delegationTokens');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('profile');
  };

  getToken = () => localStorage.getItem('token');

  getAccessToken = () => localStorage.getItem('accessToken');

  getProfile = () => {
    try { 
      return JSON.parse(localStorage.getItem('profile')) || undefined;
    } catch (e) {}
  };

  getExpiration = () => {
    try {
      return JSON.parse(localStorage.getItem('expires_at')) || undefined;
    } catch (e) {}
  };

  updateProfile = (profile) => localStorage.setItem("profile", JSON.stringify(profile));

  // unfortunate, but localStorage can fill up if this isn't called.
  // should only be called after successful authentication has completed to avoid
  // removing in process nonces
  // https://github.com/auth0/auth0.js/issues/402
  clearOldNonces = () => Object.keys(localStorage).forEach(key => {
      if(!key.startsWith('com.auth0.auth')) return;
      localStorage.removeItem(key);
    });

  // returns a proimse that resolves with an authenticated status (true, false)
  // will not redirect
  handleAuthentication = () => {
    if (this.isLoggedIn()) {
      this.setExpirationTimer();
      return _Promise.resolve(true);
    }
    if(!window.location.hash){
      return _Promise.resolve(false);
    }
    const parseHash = promisify(this.auth0.parseHash.bind(this.auth0));
    return parseHash()
      .then(authResult => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          window.location.hash = '';
          this.clearOldNonces();
          this.saveToken(authResult.idToken, authResult.accessToken, authResult.idTokenPayload, authResult.expiresIn);
          const returnUri = localStorage.getItem('returnUri');
          if(returnUri){
            localStorage.removeItem('returnUri')
          }
          window.location = returnUri || '/';
          return true;
        }
        return false;
      })
      .then(authenticated => {
        if (authenticated) {
          this.setExpirationTimer();
        }
        return authenticated;
      })
      .catch(err => {
        // apps should handle this themselves
        throw err
      });
  };

  // Checks whether the app is authenticated.  If not it automatically initiates authentication.
  // Refer to README for documentation on method arguments
  // returns a promise that resolves with the authentication status (true, false)
  // can result in redirect
  login = (options = {}) => {
    // this isn't pretty, but it makes this method backwards compatible
    // TODO: v7.0 remove backwards compatibilty
    if (typeof options === 'string') {
      options = {
        nextUri: options,
        forceLogin: false
      };
      console.warn('Calling login("string") is deprecated. Please refer to the documentation and pass an options object instead.');
    }

    const { forceLogin, nextUri, authorizeParams = {} } = options;

    if (this.isLoggedIn() && !forceLogin) {
      return _Promise.resolve(true);
    }

    let authOptions = { redirectUri: this.redirectUri, ...authorizeParams };
    let checkSession = promisify(this.auth0.checkSession.bind(this.auth0));

    // try silent sso first
    return checkSession(authOptions)
      .then(authResult => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.clearOldNonces();
          this.saveToken(authResult.idToken, authResult.accessToken, authResult.idTokenPayload, authResult.expiresIn);
          this.setExpirationTimer();
          return true;
        }
        return false;
      })
      .catch(err => {
        if(nextUri){
          localStorage.setItem('returnUri', nextUri);
        }
        this.auth0.authorize(authOptions);
        return false;
      })
  };

  /// combines handleAuthentication and login to make sure the user is logged in
  /// and parse the hash on return in on function
  /// Refer to README for documentation on method arguments
  ensureAuthentication = (options = {}) => {
    // this isn't pretty, but it makes this method backwards compatible
    // TODO: v7.0 remove backwards compatibilty
    if (typeof options === 'string') {
      console.warn('Calling ensureAuthentication("string") is deprecated. Please refer to the documentation and pass an options object instead.');
      options = {
        nextUri: options,
        forceLogin: false
      };
    }

    const { forceLogin } = options;
    if (forceLogin) {
      return this.login(options);
    }

    return this.handleAuthentication().then(authenticated => {
      if (authenticated) {
        return _Promise.resolve(true);
      }

      return this.login(options);
    });
  };

  // can result in redirect
  logout = (nextUri, logoutOfFederated) => {
    // logout locally
    this.removeToken();
    this.events.emit('logout');
    // also log out with auth0
    const returnTo = nextUri ? {returnTo: window.location.origin + nextUri} : {};
    if (logoutOfFederated) {
      this.auth0.logout({
        client_id: this.clientID,
        federated: true,
        ...returnTo
      });
    } else {
      this.auth0.logout({
        client_id: this.clientID,
        ...returnTo
      });
    }
  };

}
